import { ClientEvents } from 'common/events/ClientEvents';
import { QueryParams } from 'common/urls/QueryParams';
import React, { useCallback } from 'react';
import { toast, ToastId } from 'react-toastify';
import { ConfirmationToast, showConfirmationToast } from 'src/shared/design-system/Toast';
import { useTracker } from 'src/shared/hooks/useTracker';
import { StringParam, useQueryParams, withDefault } from 'use-query-params';

const FAVORITES_TAB_NAME = 'FAVORITES';
const SAVED_TOAST_ID = 'saved-to-favorites';
const UNFAVORITED_TOAST_ID = 'unfavorited-confirmation';

export type FavoritedSubject = 'Dish' | 'Shef';

// Wrapped in a component so `View`'s hooks run inside the toast render tree.
const SavedToFavoritesContent: React.FC<{ subject: FavoritedSubject; toastId: ToastId }> = ({
  subject: _subject,
  toastId,
}) => {
  const [, setParams] = useQueryParams({
    [QueryParams.TAB]: withDefault(StringParam, undefined),
    [QueryParams.SHEF]: StringParam,
  });
  const tracker = useTracker();

  const handleView = useCallback(() => {
    tracker.track(ClientEvents.NAV_CLICK, { elementName: 'go-to-favorites' });
    // Clear ?shef so the profile overlay closes before the favorites tab appears.
    setParams({ [QueryParams.TAB]: FAVORITES_TAB_NAME, [QueryParams.SHEF]: undefined }, 'replaceIn');
  }, [setParams, tracker]);

  return (
    <ConfirmationToast
      iconName='heartFilled'
      label='Saved to your favorites!'
      actionLabel='View'
      onAction={handleView}
      toastId={toastId}
    />
  );
};

export const showSavedToFavoritesToast = ({ subject }: { subject: FavoritedSubject }): void => {
  toast.dismiss(SAVED_TOAST_ID);
  toast(<SavedToFavoritesContent subject={subject} toastId={SAVED_TOAST_ID} />, {
    toastId: SAVED_TOAST_ID,
    position: toast.POSITION.BOTTOM_CENTER,
    autoClose: 5000,
    hideProgressBar: true,
    closeButton: false,
  });
};

export const showUnfavoritedConfirmationToast = ({
  subject,
  onUndo,
}: {
  subject: FavoritedSubject;
  onUndo: () => void;
}): void => {
  showConfirmationToast({
    toastId: UNFAVORITED_TOAST_ID,
    iconName: 'check',
    label: `${subject} removed from your favorites`,
    actionLabel: 'Undo',
    onAction: onUndo,
  });
};
